// /home/heliowallet/frontend/services/tokenBalanceService.ts
import { ethers } from 'ethers';
import { RpcPoolManager } from './rpcPoolManager';

// IMPORTANT: Always use custom provider with selected RPC, not default wagmi provider
// to avoid using thirdweb or other default RPCs

interface Token {
  address: string;
  symbol: string;
  decimals: number;
  balance?: string;
}

interface BalanceResult {
  address: string;
  balance: string;
  success: boolean;
  error?: string;
}

// Define Task interface locally since it's not exported from rpcPoolManager
interface Task<T> {
  id: string;
  execute: (provider: ethers.JsonRpcProvider) => Promise<T>;
  lastError?: Error;
}

class TokenBalanceService {
  private static instance: TokenBalanceService;
  
  private constructor() {}
  
  static getInstance(): TokenBalanceService {
    if (!TokenBalanceService.instance) {
      TokenBalanceService.instance = new TokenBalanceService();
    }
    return TokenBalanceService.instance;
  }
  
  // Fetch balances using single provider
  async fetchBalancesWithProvider(
    provider: ethers.JsonRpcProvider,
    tokens: Token[],
    walletAddress: string
  ): Promise<BalanceResult[]> {
    const results: BalanceResult[] = [];
    const batchSize = 5;
    
    for (let i = 0; i < tokens.length; i += batchSize) {
      const batch = tokens.slice(i, i + batchSize);
      
      const batchPromises = batch.map(async (token) => {
        try {
          const tokenContract = new ethers.Contract(
            token.address,
            ['function balanceOf(address) view returns (uint256)'],
            provider
          );
          
          const balance = await tokenContract.balanceOf(walletAddress);
          const formattedBalance = ethers.formatUnits(balance, token.decimals);
          
          return {
            address: token.address,
            balance: formattedBalance,
            success: true
          };
        } catch (error: any) {
          console.error(`Failed to fetch balance for ${token.symbol}:`, error.message);
          return {
            address: token.address,
            balance: '0',
            success: false,
            error: error.message
          };
        }
      });
      
      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);
      
      // Small delay between batches
      if (i + batchSize < tokens.length) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }
    
    return results;
  }
  
  // Fetch balances using RPC pool for better reliability
  async fetchBalancesWithPool(
    rpcUrls: string[],
    tokens: Token[],
    walletAddress: string
  ): Promise<BalanceResult[]> {
    if (rpcUrls.length === 0 || tokens.length === 0) {
      return [];
    }
    
    // Create tasks for RPC pool
    const tasks: Task<string>[] = tokens.map(token => ({
      id: `balance-${token.address}`,
      execute: async (provider) => {
        const tokenContract = new ethers.Contract(
          token.address,
          ['function balanceOf(address) view returns (uint256)'],
          provider
        );
        
        const balance = await tokenContract.balanceOf(walletAddress);
        return ethers.formatUnits(balance, token.decimals);
      }
    }));
    
    const pool = new RpcPoolManager(rpcUrls);
    
    try {
      const taskResults = await pool.executeBulkTasks(tasks, {
        rpcRotateInterval: 200,
        maxConcurrent: 5,
        timeout: 10000
      });
      
      // Convert task results to balance results
      const results: BalanceResult[] = [];
      
      taskResults.forEach((result: any, index: number) => {
        const token = tokens[index];
        if (result.success && result.data) {
          results.push({
            address: token.address,
            balance: result.data,
            success: true
          });
        } else {
          results.push({
            address: token.address,
            balance: '0',
            success: false,
            error: result.error?.message
          });
        }
      });
      
      return results;
      
    } finally {
      pool.destroy();
    }
  }
  
  // Fetch token info (name, symbol, decimals)
  async fetchTokenInfo(
    provider: ethers.JsonRpcProvider,
    tokenAddress: string
  ): Promise<{ name: string; symbol: string; decimals: number } | null> {
    try {
      const tokenContract = new ethers.Contract(
        tokenAddress,
        [
          'function name() view returns (string)',
          'function symbol() view returns (string)',
          'function decimals() view returns (uint8)'
        ],
        provider
      );
      
      const [name, symbol, decimals] = await Promise.all([
        tokenContract.name(),
        tokenContract.symbol(),
        tokenContract.decimals()
      ]);
      
      return {
        name,
        symbol,
        decimals: Number(decimals)
      };
    } catch (error) {
      console.error(`Failed to fetch token info for ${tokenAddress}:`, error);
      return null;
    }
  }
  
  // Batch fetch token info
  async batchFetchTokenInfo(
    rpcUrls: string[],
    tokenAddresses: string[]
  ): Promise<Map<string, { name: string; symbol: string; decimals: number }>> {
    const results = new Map();
    
    if (rpcUrls.length === 0 || tokenAddresses.length === 0) {
      return results;
    }
    
    const tasks: Task<{ name: string; symbol: string; decimals: number }>[] = tokenAddresses.map(address => ({
      id: `info-${address}`,
      execute: async (provider) => {
        const tokenContract = new ethers.Contract(
          address,
          [
            'function name() view returns (string)',
            'function symbol() view returns (string)',
            'function decimals() view returns (uint8)'
          ],
          provider
        );
        
        const [name, symbol, decimals] = await Promise.all([
          tokenContract.name(),
          tokenContract.symbol(),
          tokenContract.decimals()
        ]);
        
        return {
          name,
          symbol,
          decimals: Number(decimals)
        };
      }
    }));
    
    const pool = new RpcPoolManager(rpcUrls);
    
    try {
      const taskResults = await pool.executeBulkTasks(tasks, {
        rpcRotateInterval: 200,
        maxConcurrent: 5,
        timeout: 10000
      });
      
      taskResults.forEach((result: any, index: number) => {
        const address = tokenAddresses[index];
        if (result.success && result.data) {
          results.set(address, result.data);
        } else {
          console.error(`Failed to fetch token info for ${address}:`, result.error?.message);
        }
      });
      
      return results;
      
    } finally {
      pool.destroy();
    }
  }
  
  // Get native token balance
  async getNativeBalance(
    provider: ethers.JsonRpcProvider,
    walletAddress: string
  ): Promise<string> {
    try {
      const balance = await provider.getBalance(walletAddress);
      return ethers.formatEther(balance);
    } catch (error) {
      console.error('Failed to fetch native balance:', error);
      return '0';
    }
  }
  
  // Get native balance using RPC pool
  async getNativeBalanceWithPool(
    rpcUrls: string[],
    walletAddress: string
  ): Promise<string> {
    if (rpcUrls.length === 0) {
      return '0';
    }
    
    const task: Task<string> = {
      id: 'native-balance',
      execute: async (provider) => {
        const balance = await provider.getBalance(walletAddress);
        return ethers.formatEther(balance);
      }
    };
    
    const pool = new RpcPoolManager(rpcUrls);
    
    try {
      const result = await pool.executeTask(task);
      return result.success && result.data ? result.data : '0';
    } finally {
      pool.destroy();
    }
  }
}

export const tokenBalanceService = TokenBalanceService.getInstance();